import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, RefreshCw, Lock, Unlock, ShieldCheck, UserPlus, MapPin, Clock } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ApprovalsPanel — Superadmin-only management of:
 *   1. Accounts awaiting first-time approval
 *   2. Accounts currently IP-locked (unknown IP triggered auto-lock)
 * Both actions require Step-Up verification via the parent's withStepUp/stepUpHeaders.
 */
const ApprovalsPanel = ({ getAuthHeaders, stepUpHeaders, withStepUp }) => {
  const [pending, setPending] = useState([]);
  const [locked, setLocked] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    setLoading(true);
    try {
      const [p, l] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/security/pending-accounts`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/security/ip-locked-accounts`, { headers }).catch(() => ({ data: [] })),
      ]);
      setPending(p.data || []);
      setLocked(l.data || []);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchAll(); const t = setInterval(fetchAll, 30000); return () => clearInterval(t); }, [fetchAll]);

  const approve = (u) => withStepUp(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/security/approve-account/${u}`, {}, { headers: stepUpHeaders() });
      toast.success(`${u} approved — they can now log in`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  }, 'approving an account');

  const reject = (u) => withStepUp(async () => {
    if (!window.confirm(`Reject ${u}? This DELETES the pending account.`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/security/reject-account/${u}`, {}, { headers: stepUpHeaders() });
      toast.success(`${u} rejected & removed`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  }, 'rejecting an account');

  const unlock = (u, trustIp) => withStepUp(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/security/unlock-ip/${u}`, { trust_ip: !!trustIp }, { headers: stepUpHeaders() });
      toast.success(trustIp ? `${u} unlocked & new IP trusted` : `${u} unlocked (IP not trusted)`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Unlock failed'); }
  }, 'unlocking an account');

  const resetIps = (u) => withStepUp(async () => {
    if (!window.confirm(`Reset all trusted IPs for ${u}? They'll be IP-locked on their very next login.`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/security/reset-trusted-ips/${u}`, {}, { headers: stepUpHeaders() });
      toast.success(`Trusted IPs cleared for ${u}`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reset failed'); }
  }, 'resetting trusted IPs');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="approvals-panel">
      {/* Pending Approvals */}
      <div className="rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.04] to-transparent p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-amber-300/70">Awaiting Approval</p>
              <h3 className="text-white font-semibold text-base">Pending Accounts <span className="text-amber-300 ml-1">({pending.length})</span></h3>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={fetchAll} disabled={loading}
            className="text-slate-400 hover:text-white"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
        {pending.length === 0 ? (
          <p className="text-slate-500 text-xs text-center py-6">No accounts waiting. New sign-ups will appear here.</p>
        ) : (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {pending.map((acc) => (
              <div key={acc.id} className="p-3 rounded-xl border border-slate-800/60 bg-slate-900/40 flex items-center gap-3" data-testid={`pending-${acc.username}`}>
                <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/25 flex items-center justify-center text-cyan-300 text-xs font-semibold flex-shrink-0">
                  {(acc.display_name || acc.username).slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{acc.display_name || acc.username}</p>
                  <p className="text-slate-500 text-[11px] truncate">
                    @{acc.username} · <span className="text-cyan-400 capitalize">{acc.role}</span>
                    {acc.job_title ? ` · ${acc.job_title}` : ''}
                  </p>
                  {acc.created_by && <p className="text-[10px] text-slate-600 mt-0.5">Created by {acc.created_by}</p>}
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <Button size="sm" onClick={() => approve(acc.username)} data-testid={`approve-${acc.username}`}
                    className="h-8 px-3 text-[11px] bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25">
                    <Check className="w-3.5 h-3.5 mr-1" /> Approve
                  </Button>
                  <Button size="sm" onClick={() => reject(acc.username)} data-testid={`reject-${acc.username}`}
                    className="h-8 px-3 text-[11px] bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* IP Locked Accounts */}
      <div className="rounded-2xl border border-rose-500/25 bg-gradient-to-br from-rose-500/[0.04] to-transparent p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center">
              <Lock className="w-5 h-5 text-rose-300" />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-rose-300/70">Unknown-IP Trigger</p>
              <h3 className="text-white font-semibold text-base">IP-Locked Accounts <span className="text-rose-300 ml-1">({locked.length})</span></h3>
            </div>
          </div>
        </div>
        {locked.length === 0 ? (
          <p className="text-slate-500 text-xs text-center py-6">No accounts locked. Auto-locks trigger when a staff signs in from an unrecognised IP.</p>
        ) : (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {locked.map((acc) => (
              <div key={acc.id} className="p-3 rounded-xl border border-rose-500/20 bg-rose-500/[0.03]" data-testid={`locked-${acc.username}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-rose-500/10 border border-rose-500/25 flex items-center justify-center text-rose-300 text-xs font-semibold flex-shrink-0">
                    {(acc.display_name || acc.username).slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{acc.display_name || acc.username} <span className="text-slate-500 text-[11px]">@{acc.username}</span></p>
                    <p className="text-[11px] text-rose-300/80 flex items-center gap-1.5 mt-0.5">
                      <MapPin className="w-3 h-3" />
                      <span className="font-mono">{acc.ip_locked_from_ip}</span>
                      {acc.ip_locked_from_geo?.country && (
                        <span className="text-slate-400"> · {acc.ip_locked_from_geo.city ? `${acc.ip_locked_from_geo.city}, ` : ''}{acc.ip_locked_from_geo.country}</span>
                      )}
                    </p>
                    {acc.ip_locked_at && (
                      <p className="text-[10px] text-slate-600 mt-0.5 flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {new Date(acc.ip_locked_at).toLocaleString()}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  <Button size="sm" onClick={() => unlock(acc.username, true)} data-testid={`unlock-trust-${acc.username}`}
                    className="h-7 px-2.5 text-[10px] bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/25">
                    <Unlock className="w-3 h-3 mr-1" /> Unlock &amp; Trust IP
                  </Button>
                  <Button size="sm" onClick={() => unlock(acc.username, false)} data-testid={`unlock-only-${acc.username}`}
                    className="h-7 px-2.5 text-[10px] bg-slate-800 border border-slate-700 text-slate-300 hover:text-white">
                    <Unlock className="w-3 h-3 mr-1" /> Unlock Only
                  </Button>
                  <Button size="sm" onClick={() => resetIps(acc.username)} data-testid={`reset-ips-${acc.username}`}
                    className="h-7 px-2.5 text-[10px] bg-amber-500/10 border border-amber-500/30 text-amber-300 hover:bg-amber-500/20">
                    <ShieldCheck className="w-3 h-3 mr-1" /> Clear Trusted IPs
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalsPanel;
